/**
 * Batch 25 — Super-admin Platform Health page.
 *
 * Design: ``backend/docs/batch_25_design.md`` §4.3.
 *
 * Reads ``GET /admin/platform-health`` (60 s server cache) and composes
 * the six sub-panels. TanStack Query ``staleTime`` mirrors the server
 * cache so the React layer doesn't hammer the backend when the server
 * would return the same bytes.
 */
import { useQuery } from "@tanstack/react-query";
import { fetchPlatformHealth } from "@/lib/dashboardApi";
import type { PlatformHealthResponse } from "@/lib/dashboardApi";
import { UptimeSparkline } from "@/components/admin/platformHealth/UptimeSparkline";
import { ExceptionsList } from "@/components/admin/platformHealth/ExceptionsList";
import { IncidentsTable } from "@/components/admin/platformHealth/IncidentsTable";
import { SecretsModeCard } from "@/components/admin/platformHealth/SecretsModeCard";
import { DestructiveOpsCard } from "@/components/admin/platformHealth/DestructiveOpsCard";
import { DeepLinksCard } from "@/components/admin/platformHealth/DeepLinksCard";

function StatusBanner({
  value,
  monitoringConfigured,
}: {
  value: PlatformHealthResponse["status_banner"];
  monitoringConfigured: boolean;
}): JSX.Element {
  // P0-2 parity with the front-door banner: green + unconfigured monitoring is
  // a calm info state, never an alarm.
  if (value === "green" && !monitoringConfigured) {
    return (
      <div
        data-testid="platform-health-status-banner"
        data-monitoring="unconfigured"
        className="rounded-lg border border-border-default bg-surface-card px-3 py-1.5 text-sm font-medium text-text-tertiary"
      >
        Monitoring not configured · uptime &amp; error tracking aren't wired yet
      </div>
    );
  }
  const colour =
    value === "green"
      ? "border-brand-positive/30 bg-brand-positive/[0.06] text-brand-positive"
      : value === "yellow"
        ? "border-brand-warn/40 bg-brand-warn/[0.08] text-brand-warn"
        : "border-brand-negative/40 bg-brand-negative/[0.08] text-brand-negative";
  const label =
    value === "green"
      ? "Platform healthy"
      : value === "yellow"
        ? "Platform degraded"
        : "Platform incident";
  return (
    <div
      data-testid="platform-health-status-banner"
      data-status={value}
      className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${colour}`}
    >
      {label}
    </div>
  );
}

export default function PlatformHealth(): JSX.Element {
  const q = useQuery({
    queryKey: ["platform-health"],
    queryFn: fetchPlatformHealth,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  if (q.isLoading) {
    return (
      <div
        data-testid="platform-health-loading"
        className="rounded border border-brand-100 bg-white p-4 text-sm text-text-secondary dark:border-white/[0.06] dark:bg-surface-card dark:text-brand-100/70"
      >
        Loading…
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div
        data-testid="platform-health-error"
        className="rounded border border-red-100 bg-red-50 p-4 text-sm text-red-900 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"
      >
        Couldn't load platform health.
      </div>
    );
  }

  const data = q.data;
  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-base font-semibold tracking-tight text-text-emphasis">
          Platform health
        </h1>
        <p className="text-2xs text-text-tertiary">
          Super-admin · observability &amp; ops
        </p>
      </div>
      <StatusBanner
        value={data.status_banner}
        monitoringConfigured={data.monitoring_configured}
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <UptimeSparkline data={data.uptime_90d} />
        <ExceptionsList data={data.recent_exceptions} />
      </div>
      <IncidentsTable data={data.tenant_impacting_incidents} />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <SecretsModeCard data={data.secrets_mode} />
        <DestructiveOpsCard data={data.destructive_op_last_run} />
      </div>
      <DeepLinksCard data={data.deep_links} />
    </div>
  );
}
